import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import TopHeader from "../TopHeader";
import DebugPanel from "../DebugPanel";
import {
  CheckCircle,
  Extension,
  Flight,
  Info,
  Stars,
  Train,
} from "@material-ui/icons";
import clsx from "clsx";
import GlobalAlertTimer from "../GlobalAlertTimer";
import useMediaQuery from "hooks/useMediaQuery";

const useStyles = makeStyles((theme) => ({
  bg: {
    borderRadius: "20px",
  },
  root: {
    flexGrow: 1,
    padding: "24px",
    // height: 60,
  },
  iconRoot: {
    backgroundColor: "#eaeaf0",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "100%",
    color: "#9E9E9E",
  },
  iconActive: {
    color: "#3F51B5",
  },
  iconCompleted: {
    color: "green",
  },
  chip: {
    margin: 2,
  },

  backButton: {
    marginRight: theme.spacing(1),
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  title: {
    textAlign: "left",
  },
}));

export default function StepperComponent(props) {
  const classes = useStyles();
  const {
    onNext,
    onBack,
    onReset,
    steps,
    customerCareDescription,
    customerCarePhone,
    getStepContent,
    stepsByName,
    Sidebar,
    activeStep,
    division,
    isSubmitting = false,
    isSubmitButtonDisabled,
    sendBookingButtonLabel = "Invia Prenotazione",
    fetchingExtraServices = false,
    applicationType,
  } = props;

  const getCustomStepIcon = useCallback(
    (props) => {
      const { active, completed } = props;

      const stepIcons = {
        1: division === "2" ? <Train /> : <Flight />,
        2: <Stars />,
        3: <Info />,
        4: <Extension />,
        5: <CheckCircle />,
      };

      return (
        <div
          className={clsx(classes.iconRoot, {
            [classes.iconActive]: active,
            [classes.iconCompleted]: completed,
          })}
        >
          {stepIcons[String(props.icon)]}
        </div>
      );
    },
    [classes, division]
  );

  const { isMobile } = useMediaQuery();

  return (
    <div className="container-fluid App-body">
      <TopHeader
        division={division}
        phone={customerCarePhone}
        rightTextRows={customerCareDescription}
      />
      <br />
      <div className={`${classes.root} ${classes.bg} container v-divider-top`}>
        <DebugPanel />
        <br />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} className="step-nav-wrapper">
            <Stepper activeStep={activeStep} className="step-nav">
              {steps.map((label, idx) => {
                return (
                  <Step key={label}>
                    <StepLabel StepIconComponent={getCustomStepIcon}>
                      {label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Grid>
          {applicationType === "main" && (
            <Grid item xs={12}>
              <GlobalAlertTimer activeStep={activeStep} />
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={isMobile || activeStep === stepsByName.RECAP ? 12 : 8}
            className="box-form form-content"
          >
            <div>
              {activeStep === steps.length ? (
                <div>
                  <Typography>All steps completed</Typography>
                  <Button onClick={onReset}>Reset</Button>
                </div>
              ) : (
                <div>
                  <div>{getStepContent()}</div>
                  <div
                    className={clsx("footer-step-nav", {
                      "footer-step-nav-empty":
                        activeStep === stepsByName.RECAP,
                    })}
                  >
                    <Button
                      disabled={
                        activeStep === 0 ||
                        activeStep === stepsByName.RECAP ||
                        isSubmitting
                      }
                      onClick={onBack}
                      className={classes.backButton}
                    >
                      {activeStep === stepsByName.RECAP ? "" : "Indietro"}
                    </Button>
                    {activeStep !== stepsByName.RECAP && (
                      <Button
                        disabled={
                          activeStep === stepsByName.RECAP - 1
                            ? isSubmitButtonDisabled || fetchingExtraServices
                            : isSubmitting
                        }
                        variant="contained"
                        color={
                          activeStep === stepsByName.RECAP - 1
                            ? "secondary"
                            : activeStep === stepsByName.RECAP
                            ? "default"
                            : "primary"
                        }
                        onClick={onNext}
                        className={
                          activeStep === stepsByName.RECAP - 1
                            ? classes.button + " btn-lg btn-reserve"
                            : activeStep === stepsByName.RECAP
                            ? classes.button + " btn-success"
                            : classes.button
                        }
                      >
                        <span>
                          {activeStep === stepsByName.RECAP - 1
                            ? sendBookingButtonLabel
                            : "Avanti"}
                        </span>
                        {isSubmitting && (
                          //aggiungere solo su step stepsByName.RECAP - 1
                          <span
                            style={{
                              display: "inline-block",
                              marginLeft: "8px",
                            }}
                          >
                            {activeStep === stepsByName.RECAP - 1 && (
                              <small>Attendi caricamento dati...</small>
                            )}
                            <CircularProgress
                              size={24}
                              className={classes.buttonProgress}
                            />
                          </span>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Grid>
          {activeStep < stepsByName.RECAP && (
            <Sidebar
              onNext={onNext}
              sendBookingButtonLabel={sendBookingButtonLabel}
              isSubmitting={isSubmitting}
              stepsByName={stepsByName}
            />
          )}
        </Grid>
      </div>
    </div>
  );
}

StepperComponent.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  activeStep: PropTypes.number.isRequired,
  division: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool,
  isSubmitButtonDisabled: PropTypes.bool.isRequired,
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  customerCareDescription: PropTypes.arrayOf(PropTypes.string).isRequired,
  customerCarePhone: PropTypes.string.isRequired,
  getStepContent: PropTypes.func.isRequired,
  stepsByName: PropTypes.object.isRequired,
  Sidebar: PropTypes.elementType.isRequired,
  sendBookingButtonLabel: PropTypes.string,
  fetchingExtraServices: PropTypes.bool,
};
